import React, { Component } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, OverlayTrigger, Popover, Row, Col, Button } from 'react-bootstrap';
import cn from 'classnames';
import styles from './ticket-spot.scss';
import { InputVerificationCode } from '../pin-verify';
import { errorHander } from '../../utils/error-handler';
import { StepButtonWrapper } from './checkout-stepper';

const darkModeCardStyle = {
  '.input-container': {
    borderRadius: '6px',
  },
  '.input-container.is-focus': {
    borderColor: '#006AFF',
  },
  '.input-container.is-error': {},
  '.message-text': {
    color: '#999999',
  },
  '.message-icon': {
    color: '#999999',
  },
  '.message-text.is-error': {
    color: '#ff1600',
  },
  '.message-icon.is-error': {
    color: '#ff1600',
  },
  input: {
    color: '#000',
    fontFamily: 'helvetica neue, sans-serif',
  },
  'input::placeholder': {
    color: '#999999',
  },
  'input.is-error': {
    color: '#ff1600',
  },
  '@media screen and (max-width: 600px)': {
    input: {
      fontSize: '12px',
    },
  },
};

export class SquarePaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      card: null,
      payments: null,
      savedCodeKey: uuidv4(),
      savedValue: false,
      processing: null,
      code: null,
      loading: true,
      openPinVerification: false,
      usePOS: false,
    };
  }

  componentDidMount = async () => {
    const { locationId, checkoutValues, onResizeComponent } = this.props;

    // Check for POS checkout mode via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const isPOSCheckout = urlParams.get('pos_checkout') === 'true';

    if (isPOSCheckout) {
      this.setState({
        usePOS: true,
        loading: false,
      });
      onResizeComponent && onResizeComponent();
      return;
    }

    const appId = process.env.SQUARE_APPLICATION_ID;
    if (!window.Square) {
      throw new Error('Square.js failed to load properly');
    }
    if (+checkoutValues.cartTotal === 0) {
      this.setState({
        loading: false,
      });
      return;
    }
    let payments;
    try {
      payments = await window.Square.payments(appId, locationId);
    } catch (e) {
      errorHander(e);

      console.log('Missing card details');
    }

    try {
      const card = await this.initializeCard(payments);
      this.setState({
        card,
        payments,
        loading: false,
      });
      onResizeComponent && onResizeComponent();
    } catch (e) {
      errorHander(e);
    }
  };

  initializeCard = async (payments) => {
    const card = await payments.card({
      style: darkModeCardStyle,
    });
    await card.attach('#card-container');
    return card;
  };

  verifyBuyer = async (payments, source, verificationDetails) => {
    const verificationResults = await payments.verifyBuyer(source, verificationDetails);
    return verificationResults.token;
  };

  handlePayment = async (ev) => {
    const { currency, checkoutValues, form, session } = this.props;
    const { first_name, last_name, email } = form;
    ev && ev.preventDefault();
    this.setState({ processing: true });
    try {
      const result = await this.state.card.tokenize();
      const { postalCode } = result.details.billing;
      if (result.status === 'OK') {
        const billingContact = {
          familyName: last_name,
          givenName: first_name,
          postalCode,
          email,
        };
        const verificationDetails = {
          amount: String(checkoutValues.cartTotal),
          billingContact,
          currencyCode: currency.toUpperCase(),
        };

        const verificationToken = await this.verifyBuyer(this.state.payments, result.token, { ...verificationDetails, intent: 'CHARGE' });

        const saveCardCallback = async () => {
          const saveCardResult = await this.state.card.tokenize();
          const verificationTokenStore = await this.verifyBuyer(this.state.payments, saveCardResult.token, { billingContact, intent: 'STORE' });

          return {
            verification_token: verificationTokenStore,
            idempotency_key: session.idempotency_key,
            payment_id: saveCardResult.token,
            first_name,
            last_name,
            email,
            postal_code: postalCode,
            save_code: this.state.code,
            saved_code_key: this.state.savedCodeKey,
          };
        };
        await this.props.onPaymentSubmit(
          {
            bank_token: result.token,
            payment_type: 'square',
            verification_token: verificationToken,
            idempotency_key: session.idempotency_key,
            postal_code: postalCode,
            should_save: this.state.savedValue,
          },
          this.state.savedValue ? saveCardCallback : null
        );
        this.setState({ processing: false });
      }
    } catch (e) {
      console.error(e);
      this.setState({ processing: false });
    }
  };

  createPOSTransaction = async () => {
    const { cart, attendees, session, eventId } = this.props;

    if (!attendees || attendees.length === 0) {
      throw new Error('No attendees found');
    }

    // Use first attendee as main/billing attendee
    const mainAttendee = attendees[0].form;

    // Just pass current URL as return_url
    const returnUrl = window.location.href;

    // eslint-disable-next-line no-undef
    const response = await fetch(`${process.env.EVENTVIEWER_SERVER}/api/v1/dashboard/square-pos/create-transaction`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cart,
        eventId,
        firstName: mainAttendee.first_name,
        lastName: mainAttendee.last_name,
        email: mainAttendee.email,
        attendees,
        idempotencyKey: session.idempotency_key,
        returnUrl,
      }),
    });

    return response.json();
  };

  handlePOSPayment = async (ev) => {
    ev && ev.preventDefault();
    this.setState({ processing: true });

    try {
      const result = await this.createPOSTransaction();

      if (result.success && result.deepLink) {
        // Check if we're in development mode and provide better feedback
        const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

        if (isDevelopment) {
          console.log('=== SQUARE POS DEEP LINK ===');
          console.log('Deep Link:', result.deepLink);
          console.log('Idempotency Key:', result.idempotencyKey);
          console.log('');
          console.log('⚠️  Note: Deep link will only work on iPad with Square POS app installed.');
          console.log('');
          console.log('To test the callback flow manually, use:');
          console.log(
            `${
              process.env.EVENTVIEWER_SERVER
            }/api/v1/dashboard/square-pos/callback?status=ok&transaction_id=TEST_TX_${Date.now()}&client_transaction_id=TEST_CLIENT&state=${
              result.idempotencyKey
            }&return_url=${encodeURIComponent(window.location.href)}&event_id=${this.props.eventId}`
          );
        }

        // Attempt to redirect to Square POS app
        window.location.href = result.deepLink;

        // If we're still here after a short delay, the deep link failed (no Square POS app)
        setTimeout(() => {
          if (isDevelopment) {
            // eslint-disable-next-line no-alert, no-undef
            alert(
              'Square POS app not found.\n\n' +
                'This is expected when testing on desktop/browser.\n\n' +
                'Check browser console for manual testing instructions.'
            );
          }
          this.setState({ processing: false });
        }, 1000);
      } else {
        throw new Error(result.error || 'Failed to create POS transaction');
      }
    } catch (e) {
      errorHander(e);
      this.setState({ processing: false });
    }
  };

  togglePaymentMethod = () => {
    this.setState({ usePOS: !this.state.usePOS });
  };

  handleCashPayment = async (ev) => {
    ev && ev.preventDefault();
    this.setState({ processing: true });

    try {
      // Call normal order endpoint with payment_type: 'cash' and POS flag
      await this.props.onPaymentSubmit(
        {
          payment_type: 'cash',
          idempotency_key: this.props.session.idempotency_key,
          is_pos_checkout: true,
        },
        null
      );
      this.setState({ processing: false });
    } catch (e) {
      errorHander(e);
      this.setState({ processing: false });
    }
  };

  handleSavedCardVerification = async (ev, session) => {
    const { checkoutValues, currency, selectedCard, form } = this.props;
    const { first_name, last_name, email } = form;
    ev && ev.preventDefault();
    try {
      const billingContact = {
        familyName: last_name,
        givenName: first_name,
        email,
      };
      const verificationDetails = {
        amount: String(checkoutValues.cartTotal),
        billingContact,
        currencyCode: currency.toUpperCase(),
      };

      const verificationToken = await this.verifyBuyer(this.state.payments, selectedCard.square_card_id, {
        ...verificationDetails,
        intent: 'CHARGE',
      });

      await this.props.onPaymentSubmit({
        saved_card_id: selectedCard.id,
        verification_token: verificationToken,
        idempotency_key: session.idempotency_key,
      });
      this.setState({ processing: false });
    } catch (e) {
      errorHander(e);

      this.setState({ processing: false });
    }
  };

  handleCheckboxChange = () => {
    this.setState({
      savedValue: !this.state.savedValue,
    });
  };

  togglePinVerification = () => {
    this.setState({
      openPinVerification: !this.state.openPinVerification,
    });
  };

  handleCodeSubmit = async (code) => {
    this.setState({
      code,
    });
    this.togglePinVerification();
    await this.handlePayment();
  };
  render() {
    const { session, selectedCard, onBack, settings, form, onPaymentSubmit, checkoutValues } = this.props;
    const { email } = form;

    if (+checkoutValues.cartTotal === 0) {
      return (
        <div className={styles.paymentButtonContainer}>
          {!this.state.savedValue &&
            React.cloneElement(this.props.children, {
              onClick: () => onPaymentSubmit({ idempotency_key: session.idempotency_key }),
            })}
        </div>
      );
    }

    const popover = (
      <Popover id="popover-basic" className={styles.popover}>
        <Popover.Content className={styles.detailsContent}>
          <InputVerificationCode saved_code_key={this.state.savedCodeKey} email={email} settings={settings} onSubmit={this.handleCodeSubmit} />
        </Popover.Content>
      </Popover>
    );

    if (session && session.cards && selectedCard) {
      return (
        <>
          <div className={styles.cardView}>
            <div className={cn('saved-card')}>
              {selectedCard.card_brand} ****-****-****-{selectedCard.last_4}
            </div>
            <div className={cn('saved-date')}>
              {selectedCard.exp_month}/{selectedCard.exp_year}
            </div>
          </div>
          <Row>
            <Col xs={12}>
              <Row className={styles.confirmQuestionsButton}>
                {React.cloneElement(this.props.children, {
                  onClick: (e) => this.handleSavedCardVerification(e, session),
                })}
              </Row>
            </Col>
          </Row>
          <div id="card-container" style={{ visibility: 'hidden' }} />
        </>
      );
    }
    return (
      <>
        <form id="payment-form">
          {!this.state.usePOS && <div id="card-container" />}
          <div className={styles.confirmQuestionsButton}>
            {this.state.savedValue && !this.state.usePOS && (
              <OverlayTrigger trigger="click" placement="top" overlay={popover} show={this.state.openPinVerification}>
                {React.cloneElement(this.props.children, {
                  onClick: this.togglePinVerification,
                  disabled: this.state.loading || this.state.processing,
                })}
              </OverlayTrigger>
            )}
            <div className={styles.paymentButtonContainer}>
              <StepButtonWrapper>
                <Button variant="outline-dark" onClick={onBack} className="checkout-prev-button checkout-back-button">
                  {settings.get('checkoutBackButtonText')}
                </Button>
              </StepButtonWrapper>
              {!this.state.savedValue && this.state.usePOS && settings.get('_posConfirmation_enableCashPayment') && (
                <div className={styles.cashCreditButton}>
                  {React.cloneElement(this.props.children, {
                    disabled: this.state.loading || this.state.processing,
                    onClick: this.handlePOSPayment,
                    children: settings.get('_posConfirmation_cardButtonText') || 'Card',
                  })}
                  {React.cloneElement(this.props.children, {
                    disabled: this.state.loading || this.state.processing,
                    onClick: this.handleCashPayment,
                    children: settings.get('_posConfirmation_cashButtonText') || 'Cash',
                  })}
                </div>
              )}
              {!this.state.savedValue &&
                (!this.state.usePOS || !settings.get('_posConfirmation_enableCashPayment')) &&
                React.cloneElement(this.props.children, {
                  disabled: this.state.loading || this.state.processing,
                  onClick: this.state.usePOS ? this.handlePOSPayment : this.handlePayment,
                })}
            </div>
            {!this.state.usePOS && settings.get('enableFastCheckout') && (
              <Form.Group controlId="formBasicCheckbox" className={styles.checkoutSaveCheckbox}>
                <Form.Check
                  type="checkbox"
                  value={this.state.savedValue}
                  label={settings.get('fastCheckoutSaveDetails')}
                  onChange={this.handleCheckboxChange}
                  disabled={this.state.loading}
                />
              </Form.Group>
            )}
          </div>
        </form>
        <div id="payment-status-container" />
      </>
    );
  }
}
